/**
 * Meta Pixel destination (browser-side).
 *
 * The pixel script is loaded in `index.html`, but `fbq('init')`, PageView and
 * any subsequent tracking are deferred until the user grants Marketing
 * consent via the cookie banner (LGPD compliance).
 *
 * Server-side CAPI events are forwarded by the `analytics-emit` edge function
 * (independent path, deduped via event_id).
 */
import type { AnalyticsDestination, EventName, SuperProperties } from '../types';
import { marketingAllowed } from '../../cookie-prefs';

const PIXEL_ID = '937660719140565';

const STANDARD_MAP: Partial<Record<EventName, string>> = {
  signup_completed: 'CompleteRegistration',
  pricing_viewed: 'ViewContent',
  pricing_plan_clicked: 'InitiateCheckout',
  subscription_checkout_started: 'InitiateCheckout',
  subscription_activated: 'Subscribe',
};

let superProps: SuperProperties = {};
let initialized = false;

declare global {
  interface Window { fbq?: (...args: any[]) => void }
}

const fbqAvailable = () => typeof window !== 'undefined' && typeof window.fbq === 'function';

const ensureInit = () => {
  if (!fbqAvailable()) return false;
  if (!marketingAllowed()) return false;
  if (!initialized) {
    try {
      window.fbq!('consent', 'grant');
      window.fbq!('init', PIXEL_ID);
      window.fbq!('track', 'PageView');
      initialized = true;
    } catch (e) {
      console.warn('[Analytics:Meta] init failed', e);
      return false;
    }
  }
  return true;
};

const refreshConsent = () => {
  if (!fbqAvailable()) return;
  if (marketingAllowed()) {
    ensureInit();
  } else {
    try { window.fbq!('consent', 'revoke'); } catch { /* noop */ }
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('careos:cookie-prefs-changed', refreshConsent);
}

export const metaDestination: AnalyticsDestination = {
  name: 'meta',

  init() {
    refreshConsent();
  },
  isReady() { return fbqAvailable() && marketingAllowed(); },
  identify() { /* noop — handled via Advanced Matching server-side (CAPI) */ },
  reset() { /* noop */ },

  track(event, props) {
    if (!ensureInit()) return;
    const std = STANDARD_MAP[event as EventName];
    const payload = { ...superProps, ...props };
    // event_id allows browser↔CAPI dedup if the server sends the same id.
    const eventID = (payload as any).event_id as string | undefined;
    if (std) window.fbq!('track', std, payload, eventID ? { eventID } : undefined);
    else window.fbq!('trackCustom', event, payload, eventID ? { eventID } : undefined);
  },

  trackScreen(name, props) {
    if (!ensureInit()) return;
    window.fbq!('trackCustom', 'ScreenView', { ...superProps, ...props, screen_name: name });
  },

  setSuperProperties(props) {
    superProps = { ...superProps, ...props };
  },
};
